import { useState, useEffect, useCallback } from "react";
import useSWR from 'swr';
import axios from "axios";
import TabPageLayout, { PageLoader, DataCard, EmptyState } from "./TabPageLayout";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Building2, Mail, Phone, MapPin, Calendar, Grid, List, Search, Edit, Check, X, User, Lock, Eye, EyeOff, Trash2, AlertTriangle, Plus, Minus, Zap, Database, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { API_URL } from "../config/api";

// Use PostgreSQL v2 API for super admin operations
const PLATFORM_API = `${API_URL}/v2/super-admin`;

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

// SWR Fetcher for PostgreSQL v2 endpoints
const fetcher = async (url) => {
  const token = localStorage.getItem("jwtToken");
  const response = await axios.get(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  // v2 endpoints return { organizations: [...] }, extract the array
  if (response.data.organizations) {
    return response.data.organizations;
  }
  return response.data;
};

const OrganizationsManagement = () => {
  const [viewMode, setViewMode] = useState("list");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCity, setFilterCity] = useState("all");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [editingOrg, setEditingOrg] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [adminUser, setAdminUser] = useState(null);
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [showActionsDialog, setShowActionsDialog] = useState(false);
  const [selectedOrgForActions, setSelectedOrgForActions] = useState(null);
  const [actionAmount, setActionAmount] = useState(0);
  const [actionType, setActionType] = useState("add");

  // Debounced search (300ms)
  const debouncedSearch = useDebounce(searchTerm, 300);

  // Fetch organizations with SWR
  const { data: organizations = [], error, isLoading, mutate } = useSWR(
    `${PLATFORM_API}/organizations`,
    fetcher,
    { revalidateOnFocus: true, refreshInterval: 30000 }
  );

  // Fetch locations (less critical, can use simple SWR)
  const { data: locations = [] } = useSWR(
    `${PLATFORM_API}/locations`,
    fetcher
  );

  // Legacy functions that need mutate
  const fetchOrganizations = () => mutate();

  // Filter and sort organizations
  const filteredOrgs = organizations
    .filter(org => {
      // Text search (using debounced value)
      const matchesSearch = 
        org.name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        org.slug?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        org.ownerName?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
        org.ownerEmail?.toLowerCase().includes(debouncedSearch.toLowerCase());
      
      // Status filter
      const matchesStatus = filterStatus === "all" || org.status === filterStatus;
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let compareA, compareB;
      
      switch(sortBy) {
        case "name":
          compareA = a.name?.toLowerCase() || "";
          compareB = b.name?.toLowerCase() || "";
          break;
        case "ownerName":
          compareA = a.ownerName?.toLowerCase() || "";
          compareB = b.ownerName?.toLowerCase() || "";
          break;
        case "city":
          compareA = a.city?.toLowerCase() || "";
          compareB = b.city?.toLowerCase() || "";
          break;
        case "status":
          compareA = a.status?.toLowerCase() || "";
          compareB = b.status?.toLowerCase() || "";
          break;
        case "createdAt":
          compareA = new Date(a.createdAt);
          compareB = new Date(b.createdAt);
          break;
        default:
          return 0;
      }
      
      if (sortOrder === "asc") {
        return compareA > compareB ? 1 : -1;
      } else {
        return compareA < compareB ? 1 : -1;
      }
    });

  // Handle suspend/unsuspend
  const handleSuspendToggle = async (org) => {
    const newStatus = org.status === 'suspended' ? 'active' : 'suspended';
    const action = newStatus === 'suspended' ? 'suspend' : 'unsuspend';
    
    if (!window.confirm(`Are you sure you want to ${action} ${org.name}?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem("jwtToken");
      // Map status to v2 endpoint action
      let endpoint;
      if (newStatus === 'active') {
        endpoint = `${PLATFORM_API}/organizations/${org.id}/activate`;
      } else if (newStatus === 'suspended') {
        endpoint = `${PLATFORM_API}/organizations/${org.id}/suspend`;
      } else {
        // Fallback to PATCH for other status changes
        await axios.patch(
          `${PLATFORM_API}/organizations/${org.id}`,
          { status: newStatus },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Organization ${action}ed successfully!`);
        fetchOrganizations();
        return;
      }
      
      await axios.post(endpoint, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Organization ${action}ed successfully!`);
      fetchOrganizations();
    } catch (error) {
      toast.error(`Failed to ${action} organization`);
    }
  };
  
  // Handle approval - PostgreSQL v2 (no separate DB creation needed)
  const handleApprove = async (org, approvalStatus = 'active') => {
    if (!window.confirm(`Approve and activate ${org.name}?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem("jwtToken");
      
      // PostgreSQL v2: Just approve the org (no separate DB needed)
      const response = await axios.post(
        `${PLATFORM_API}/organizations/${org.id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`✅ ${org.name} is now approved and active!`);
      fetchOrganizations();
    } catch (error) {
      console.error('Activation error:', error);
      toast.error(error.response?.data?.detail || "Failed to approve organization");
    }
  };

  // Handle super admin login as organization
  const handleLoginAsOrg = async (org) => {
    if (!window.confirm(`Login as ${org.name}? This will redirect you to their dashboard.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem("jwtToken");
      const response = await axios.post(
        `${PLATFORM_API}/organizations/${org.id}/super-admin-login`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        // Store the org's JWT token
        localStorage.setItem("jwtToken", response.data.token);
        localStorage.setItem("userType", "operator");
        localStorage.setItem("userData", JSON.stringify({
          username: response.data.username,
          name: response.data.name,
          organizationId: org.id
        }));
        
        toast.success(`Logged in as ${org.name}`);
        // Redirect to their dashboard
        window.location.href = "/admin";
      }
    } catch (error) {
      console.error("Error logging in as org:", error);
      toast.error("Failed to login as organization");
    }
  };

  // Handle add actions
  const handleAddActions = (org) => {
    setSelectedOrgForActions(org);
    setActionType("add");
    setActionAmount(0);
    setShowActionsDialog(true);
  };

  // Handle remove actions
  const handleRemoveActions = (org) => {
    setSelectedOrgForActions(org);
    setActionType("remove");
    setActionAmount(0);
    setShowActionsDialog(true);
  };

  // Save action changes - PostgreSQL v2
  const handleSaveActions = async () => {
    if (!selectedOrgForActions || actionAmount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      const token = localStorage.getItem("jwtToken");
      const amount = actionType === "add" ? actionAmount : -actionAmount;

      await axios.post(
        `${PLATFORM_API}/organizations/${selectedOrgForActions.id}/actions`,
        { amount: amount, reason: `Manual ${actionType} by super admin` },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${actionType === "add" ? "Added" : "Removed"} ${actionAmount} actions`);
      setShowActionsDialog(false);
      fetchOrganizations(); // Refresh list
    } catch (error) {
      console.error("Error updating actions:", error);
      toast.error("Failed to update actions");
    }
  };


  // Handle edit
  const handleEdit = async (org) => {
    setEditingOrg({...org});
    setNewAdminPassword("");
    setShowPassword(false); // Reset password visibility
    
    // Fetch admin user details
    try {
      const token = localStorage.getItem("jwtToken");
      const response = await axios.get(
        `${PLATFORM_API}/organizations/${org.id}/admin-user`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const userData = response.data;
      // Store original username for comparison
      setAdminUser({
        ...userData,
        originalUsername: userData.username
      });
    } catch (error) {
      console.error("Error fetching admin user:", error);
      // Set default if fetch fails
      setAdminUser({ 
        username: 'admin',
        originalUsername: 'admin'
      });
    }
    
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem("jwtToken");
      
      if (!token) {
        toast.error("Authentication required. Please login again.");
        return;
      }
      
      // Update organization details
      await axios.put(
        `${PLATFORM_API}/organizations/${editingOrg.id}`,
        editingOrg,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update admin user if username or password changed
      const adminUpdates = {};
      if (adminUser && adminUser.username !== adminUser.originalUsername) {
        adminUpdates.username = adminUser.username;
      }
      if (newAdminPassword) {
        adminUpdates.password = newAdminPassword;
      }
      
      if (Object.keys(adminUpdates).length > 0) {
        await axios.put(
          `${PLATFORM_API}/organizations/${editingOrg.id}/admin-user`,
          adminUpdates,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success("Organization updated!");
      setShowEditDialog(false);
      setNewAdminPassword("");
      fetchOrganizations();
    } catch (error) {
      console.error("Error updating organization:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to update organization";
      toast.error(errorMsg);
    }
  };

  const handleDeleteOrg = async (org) => {
    // Determine deletion stage based on current status
    const currentStatus = org.status;
    let action = "";
    let newStatus = "";
    let confirmMessage = "";
    
    if (currentStatus !== 'suspended' && currentStatus !== 'soft_deleted') {
      toast.error("Organization must be suspended before deletion. Please suspend it first.");
      return;
    }
    
    if (currentStatus === 'suspended') {
      action = "soft delete";
      newStatus = "soft_deleted";
      confirmMessage = `Soft delete "${org.name}"? This will hide the organization but preserve all data. This action can be reversed.`;
    } else if (currentStatus === 'soft_deleted') {
      action = "permanently delete";
      newStatus = "deleted";
      confirmMessage = `PERMANENTLY DELETE "${org.name}"? This will CASCADE DELETE all data including teamMembers, assets, trips, and bookings. THIS CANNOT BE UNDONE!`;
    }
    
    setOrgToDelete({ org, action, newStatus, confirmMessage });
    setDeleteConfirmDialog(true);
  };

  const confirmDelete = async () => {
    // For hard delete, require typing "Delete"
    if (orgToDelete?.newStatus === 'deleted' && deleteConfirmText !== 'Delete') {
      toast.error('Please type "Delete" to confirm permanent deletion');
      return;
    }

    try {
      const token = localStorage.getItem("jwtToken");
      const { org, newStatus } = orgToDelete;
      
      // Both soft delete and hard delete use the DELETE endpoint
      // The backend determines the action based on current org status
      const response = await axios.delete(
        `${PLATFORM_API}/organizations/${org.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Show appropriate success message
      if (response.data.stage === "soft_delete") {
        toast.success(`Organization "${org.name}" soft deleted (reversible)`);
      } else if (response.data.stage === "hard_delete") {
        toast.success(`Organization "${org.name}" permanently deleted`);
        
        // Show deleted counts if available
        if (response.data.deleted_counts) {
          const counts = response.data.deleted_counts;
        }
      }
      
      setDeleteConfirmDialog(false);
      setOrgToDelete(null);
      setDeleteConfirmText("");
      fetchOrganizations();
    } catch (error) {
      console.error("Error deleting organization:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to delete organization";
      toast.error(errorMsg);
    }
  };

  const handleCleanupData = async (org) => {
    if (!window.confirm(`Clean up all data for "${org.name}"? This will remove all bookings, team members, assets, etc. from their database.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem("jwtToken");
      const response = await axios.post(
        `${PLATFORM_API}/organizations/${org.id}/cleanup-orphaned-data`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`✅ Cleaned up ${response.data.documentsRemoved} documents`);
      fetchOrganizations();
    } catch (error) {
      console.error("Cleanup error:", error);
      toast.error(error.response?.data?.detail || "Failed to cleanup data");
    }
  };



  // Status badge color
  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'suspended': return 'bg-red-100 text-red-700';
      case 'soft_deleted': return 'bg-gray-100 text-gray-600';
      case 'trial': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  // Stats
  const stats = {
    total: organizations.length,
    active: organizations.filter(o => o.status === 'active').length,
    pending: organizations.filter(o => o.status === 'pending').length,
    trial: organizations.filter(o => o.status === 'trial').length,
  };

  return (
    <TabPageLayout
      title="Accounts"
      subtitle={`${stats.total} organizations • ${stats.pending} pending approval`}
      loading={isLoading}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => mutate()} className="h-9">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <div className="flex border border-gray-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 ${viewMode === "list" ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              <List className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("card")}
              className={`p-2 ${viewMode === "card" ? 'bg-gray-900 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
            >
              <Grid className="w-4 h-4" />
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Search and Filters */}
        <DataCard>
          <div className="space-y-4">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-10"
              />
            </div>
            
            {/* Status Filter Chips */}
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'all', label: 'All', count: stats.total },
                { value: 'active', label: 'Active', count: stats.active },
                { value: 'pending', label: 'Pending', count: stats.pending },
                { value: 'trial', label: 'Trial', count: stats.trial },
                { value: 'suspended', label: 'Suspended' }
              ].map((status) => (
                <button
                  key={status.value}
                  onClick={() => setFilterStatus(status.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    filterStatus === status.value
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status.label}
                  {status.count !== undefined && ` (${status.count})`}
                </button>
              ))}
            </div>
          </div>
        </DataCard>

      {/* Organizations Display */}
      {viewMode === "card" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredOrgs.map(org => (
            <DataCard key={org.id} className="hover:border-blue-300 transition-colors">
              <div className="space-y-4">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-bold text-lg text-gray-900">{org.name}</h3>
                    <p className="text-sm text-gray-500 font-mono">{org.slug}</p>
                  </div>
                  <Badge className={getStatusColor(org.status)}>
                    {org.status}
                  </Badge>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-700">
                    <User className="h-4 w-4 text-gray-400" />
                    <span>{org.ownerName}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span className="truncate">{org.ownerEmail}</span>
                  </div>
                  {org.ownerPhone && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <span>{org.ownerPhone}</span>
                    </div>
                  )}
                  {org.city && (
                    <div className="flex items-center gap-2 text-gray-700">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span>{org.city}</span>
                    </div>
                  )}
                  {org.status === 'active' && org.dbName && (
                    <div className="flex items-center gap-2 text-gray-700 pt-2">
                      <Database className="h-4 w-4 text-green-600" />
                      <span className="text-xs font-mono text-green-600">{org.dbName}</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2 pt-2 border-t border-gray-100">
                  {org.status === 'pending' ? (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleEdit(org)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handleApprove(org)}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Activate
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleEdit(org)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        className={org.status === 'suspended' 
                          ? 'flex-1 bg-green-600 hover:bg-green-700 text-white' 
                          : 'flex-1 bg-red-600 hover:bg-red-700 text-white'}
                        onClick={() => handleSuspendToggle(org)}
                      >
                        {org.status === 'suspended' ? 'Activate' : 'Suspend'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </DataCard>
          ))}
        </div>
      ) : (
        <DataCard className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr>
                  <th 
                  className="px-4 py-3 text-left text-sm font-semibold cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => {
                    if (sortBy === 'name') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortBy('name');
                      setSortOrder('asc');
                    }
                  }}
                >
                  <div className="flex items-center gap-1">
                    Organization
                    {sortBy === 'name' && (
                      <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Owner</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Contact</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                <th 
                  className="px-4 py-3 text-left text-sm font-semibold cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => {
                    if (sortBy === 'createdAt') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortBy('createdAt');
                      setSortOrder('desc');
                    }
                  }}
                >
                  <div className="flex items-center gap-1">
                    Created
                    {sortBy === 'createdAt' && (
                      <span className="text-xs">{sortOrder === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-right text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredOrgs.map(org => (
                <tr key={org.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div>
                      <div className="font-semibold text-gray-900">{org.name}</div>
                      <div className="text-sm text-gray-500 font-mono">{org.slug}</div>
                      {org.status === 'active' && org.dbName && (
                        <div className="flex items-center gap-1 mt-1">
                          <Database className="h-3 w-3 text-green-600" />
                          <span className="text-xs font-mono text-green-600">{org.dbName}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">{org.ownerName}</div>
                      <div className="text-gray-600">{org.ownerEmail}</div>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-700">{org.ownerPhone || '—'}</td>
                  <td className="px-4 py-4">
                    <Badge className={getStatusColor(org.status)}>
                      {org.status}
                    </Badge>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">
                    {new Date(org.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {org.status === 'pending' ? (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(org)}
                            className="w-20"
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            className="w-24 bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleApprove(org)}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Activate
                          </Button>
                        </>
                      ) : org.status === 'suspended' ? (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(org)}
                            className="w-20"
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            className="w-24 bg-orange-600 hover:bg-orange-700 text-white"
                            onClick={() => handleCleanupData(org)}
                            title="Clean up all data"
                          >
                            Clean Data
                          </Button>
                          <Button
                            size="sm"
                            className="w-20 bg-red-600 hover:bg-red-700 text-white"
                            onClick={() => handleDeleteOrg(org)}
                            title="Soft delete organization"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </Button>
                          <Button
                            size="sm"
                            className="w-24 bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => handleSuspendToggle(org)}
                          >
                            Activate
                          </Button>
                        </>
                      ) : org.status === 'soft_deleted' ? (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(org)}
                            className="w-20"
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            className="w-32 bg-red-700 hover:bg-red-800 text-white"
                            onClick={() => handleDeleteOrg(org)}
                            title="Permanently delete - cannot be undone!"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete Forever
                          </Button>
                          <Button
                            size="sm"
                            className="w-24 bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => handleSuspendToggle(org)}
                          >
                            Restore
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(org)}
                            className="w-20"
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            className="w-24 bg-red-600 hover:bg-red-700 text-white"
                            onClick={() => handleSuspendToggle(org)}
                          >
                            Suspend
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </DataCard>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-xl">Edit Organization</DialogTitle>
          </DialogHeader>
          {editingOrg && (
            <div className="space-y-6 max-h-[70vh] overflow-y-auto px-1 pr-3">
              {/* Organization Info */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label>Company Name *</Label>
                    <Input
                      value={editingOrg.name}
                      onChange={(e) => setEditingOrg({...editingOrg, name: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Shortname (Slug) *</Label>
                    <Input
                      value={editingOrg.slug}
                      className="font-mono mt-2 bg-gray-50"
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">Cannot be changed</p>
                  </div>
                </div>

                <div>
                  <Label>Status</Label>
                  <select
                    value={editingOrg.status}
                    onChange={(e) => setEditingOrg({...editingOrg, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md mt-2"
                  >
                    <option value="pending">Pending</option>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
                
                {/* Database Name */}
                {editingOrg.dbName && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                    <Database className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-xs text-green-700 font-medium">Database</p>
                      <p className="font-mono text-sm text-green-800 font-bold">{editingOrg.dbName}</p>
                    </div>
                  </div>
                )}
                {!editingOrg.dbName && editingOrg.status === 'pending' && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-yellow-800">
                      Database not created. Click &quot;Create DB &amp; Activate&quot; to initialize.
                    </p>
                  </div>
                )}
              </div>

              {/* Owner Details */}
              <div className="pt-4 border-t border-gray-200 space-y-4">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <User className="h-4 w-4 text-gray-600" />
                  Owner Details
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <Label>Owner Name *</Label>
                    <Input
                      value={editingOrg.ownerName}
                      onChange={(e) => setEditingOrg({...editingOrg, ownerName: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label>Owner Email *</Label>
                    <Input
                      type="email"
                      value={editingOrg.ownerEmail}
                      onChange={(e) => setEditingOrg({...editingOrg, ownerEmail: e.target.value})}
                      className="mt-2"
                    />
                  </div>
                </div>
                <div>
                  <Label>Owner Phone</Label>
                  <Input
                    value={editingOrg.ownerPhone || ''}
                    onChange={(e) => setEditingOrg({...editingOrg, ownerPhone: e.target.value})}
                    placeholder="+1-555-1234"
                    className="mt-2"
                  />
                </div>
              </div>

              {/* Admin Login */}
              {adminUser && (
                <div className="pt-4 border-t border-gray-200 space-y-4">
                  <h3 className="font-semibold text-sm flex items-center gap-2">
                    <Lock className="h-4 w-4 text-gray-600" />
                    Admin Login
                  </h3>
                  <div>
                    <Label>Admin Username *</Label>
                    <Input
                      value={adminUser.username}
                      onChange={(e) => setAdminUser({...adminUser, username: e.target.value})}
                      className="font-mono mt-2"
                    />
                  </div>
                  <div>
                    <Label>Reset Password (Optional)</Label>
                    <div className="relative mt-2">
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={newAdminPassword}
                        onChange={(e) => setNewAdminPassword(e.target.value)}
                        placeholder="Leave blank to keep current"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Credits / Actions Section */}
              <div className="pt-4 border-t border-gray-200 space-y-4">
                <h3 className="font-semibold text-sm flex items-center gap-2">
                  <Zap className="h-4 w-4 text-yellow-500" />
                  Credits (Processing Actions)
                </h3>
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm text-gray-600">Current Balance</span>
                    <span className="text-xl font-bold text-yellow-700">{editingOrg.actions_balance || 0}</span>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      min="1"
                      placeholder="Amount"
                      value={actionAmount || ''}
                      onChange={(e) => setActionAmount(parseInt(e.target.value) || 0)}
                      className="w-24"
                    />
                    <Button
                      size="sm"
                      onClick={async () => {
                        if (!actionAmount || actionAmount <= 0) return;
                        try {
                          const token = localStorage.getItem("jwtToken");
                          await axios.post(
                            `${PLATFORM_API}/organizations/${editingOrg.id}/actions`,
                            { amount: actionAmount, reason: "Manual adjustment by super admin" },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          toast.success(`Added ${actionAmount} credits`);
                          setEditingOrg({...editingOrg, actions_balance: (editingOrg.actions_balance || 0) + actionAmount});
                          setActionAmount(0);
                          fetchOrganizations();
                        } catch (error) {
                          toast.error("Failed to add credits");
                        }
                      }}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Add
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        if (!actionAmount || actionAmount <= 0) return;
                        try {
                          const token = localStorage.getItem("jwtToken");
                          await axios.post(
                            `${PLATFORM_API}/organizations/${editingOrg.id}/actions`,
                            { amount: -actionAmount, reason: "Manual removal by super admin" },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          toast.success(`Removed ${actionAmount} credits`);
                          setEditingOrg({...editingOrg, actions_balance: Math.max(0, (editingOrg.actions_balance || 0) - actionAmount)});
                          setActionAmount(0);
                          fetchOrganizations();
                        } catch (error) {
                          toast.error("Failed to remove credits");
                        }
                      }}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between pt-6 border-t border-gray-200">
                <div>
                  {editingOrg.status === 'active' && (
                    <Button 
                      variant="outline" 
                      onClick={() => handleLoginAsOrg(editingOrg)}
                      className="bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                    >
                      <User className="h-4 w-4 mr-2" />
                      Login as
                    </Button>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveEdit} className="bg-blue-600 hover:bg-blue-700">
                    Save Changes
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmDialog} onOpenChange={setDeleteConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Confirm Deletion
            </DialogTitle>
          </DialogHeader>
          {orgToDelete && (
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                {orgToDelete.confirmMessage}
              </p>
              
              {orgToDelete.newStatus === 'deleted' && (
                <>
                  <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
                    <p className="text-xs font-semibold text-red-800 mb-2">⚠️ WARNING: This will delete:</p>
                    <ul className="text-xs text-red-700 space-y-1 ml-4">
                      <li>• All teamMembers in this organization</li>
                      <li>• All assets in this organization</li>
                      <li>• All trips and bookings</li>
                      <li>• All time entries</li>
                      <li>• All services and custom data</li>
                    </ul>
                    <p className="text-xs font-bold text-red-900 mt-2">THIS ACTION CANNOT BE UNDONE!</p>
                  </div>
                  
                  <div>
                    <Label htmlFor="deleteConfirm" className="text-sm font-semibold text-gray-700">
                      Type &quot;Delete&quot; to confirm permanent deletion:
                    </Label>
                    <Input
                      id="deleteConfirm"
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="Delete"
                      className="mt-2"
                    />
                  </div>
                </>
              )}
              
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeleteConfirmDialog(false);
                    setOrgToDelete(null);
                    setDeleteConfirmText("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDelete}
                  disabled={orgToDelete.newStatus === 'deleted' && deleteConfirmText !== 'Delete'}
                  className={orgToDelete.newStatus === 'deleted' 
                    ? 'bg-red-600 hover:bg-red-700 disabled:opacity-50' 
                    : 'bg-orange-600 hover:bg-orange-700'}
                >
                  {orgToDelete.newStatus === 'deleted' ? 'Permanently Delete' : 'Soft Delete'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Actions Management Dialog */}
      <Dialog open={showActionsDialog} onOpenChange={setShowActionsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-500" />
              {actionType === "add" ? "Add" : "Remove"} Actions - {selectedOrgForActions?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Current Balance</Label>
              <div className="text-2xl font-bold text-blue-600">
                {selectedOrgForActions?.has_unlimited_actions ? (
                  "∞ Unlimited"
                ) : (
                  `${selectedOrgForActions?.actions_balance || 0} credits`
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="actionAmount">
                Amount to {actionType === "add" ? "Add" : "Remove"}
              </Label>
              <Input
                id="actionAmount"
                type="number"
                min="0"
                value={actionAmount}
                onChange={(e) => setActionAmount(parseInt(e.target.value) || 0)}
                placeholder="Enter amount..."
                className="mt-1"
              />
            </div>

            {actionType === "add" && (
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
                <strong>Note:</strong> This will credit actions to the organization&apos;s balance.
              </div>
            )}

            {actionType === "remove" && (
              <div className="bg-orange-50 border border-orange-200 rounded p-3 text-sm text-orange-800">
                <strong>Warning:</strong> This will deduct actions from the organization&apos;s balance.
              </div>
            )}

            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowActionsDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveActions}
                className={actionType === "add" ? "bg-green-600 hover:bg-green-700" : "bg-orange-600 hover:bg-orange-700"}
              >
                {actionType === "add" ? "Add" : "Remove"} Actions
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {filteredOrgs.length === 0 && (
        <EmptyState
          icon={<Building2 />}
          title="No organizations found"
          description={debouncedSearch ? `No results for "${debouncedSearch}"` : "Organizations will appear here once they sign up."}
        />
      )}
      </div>
    </TabPageLayout>
  );
};

export default OrganizationsManagement;
